import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useFirmwareStore } from '../stores/firmwareStore';
import { useSerialStore } from '../stores/serialStore';
import { useLogStore } from '../stores/logStore';
import { serialService } from '../services/serialService';

const BOOTLOADER_REGEX = /BOOTLOADER:\s*([^,]+),\s*programming:\s*(\d+)%/;
const BOOTLOADER_TIMEOUT_MS = 2000;

export const FirmwareUpdate: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bufferRef = useRef<string>('');
  const [bootloaderTimedOut, setBootloaderTimedOut] = useState(false);

  const {
    fileName,
    status,
    isUploading,
    isInBootloader,
    bootloaderVersion,
    programmingProgress,
    setFilePath,
    setFileName,
    setProgress,
    setStatus,
    setIsUploading,
    setBootloaderState,
    clearBootloaderState,
  } = useFirmwareStore();

  const isConnected = useSerialStore((state) => state.isConnected);
  const addLog = useLogStore((state) => state.addLog);

  // Parse bootloader messages from serial data
  useEffect(() => {
    if (!isConnected) {
      clearBootloaderState();
      setBootloaderTimedOut(false);
      return;
    }

    const handleSerialData = (chunk: string) => {
      bufferRef.current += chunk;

      const lines = bufferRef.current.split('\n');
      bufferRef.current = lines.pop() || '';

      for (const line of lines) {
        const match = line.match(BOOTLOADER_REGEX);
        if (match) {
          const version = match[1].trim();
          const prog = parseInt(match[2], 10);
          setBootloaderState(version, prog);
          setBootloaderTimedOut(false);
        }
      }
    };

    const unsubscribe = serialService.onData(handleSerialData);

    return () => {
      unsubscribe();
      bufferRef.current = '';
    };
  }, [isConnected, setBootloaderState, clearBootloaderState]);

  // Check for bootloader timeout
  useEffect(() => {
    if (!isConnected) return;

    const intervalId = setInterval(() => {
      const lastTime = useFirmwareStore.getState().lastBootloaderMessageTime;
      if (lastTime !== null && Date.now() - lastTime > BOOTLOADER_TIMEOUT_MS) {
        setBootloaderTimedOut(true);
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, [isConnected]);

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setFilePath(file.name);
      addLog(`Selected: ${file.name}`, 'info');
    }
  };

  const handleStartUpdate = async () => {
    if (!isConnected) {
      setStatus('No serial port connected');
      addLog('No serial port chosen', 'error');
      return;
    }

    if (!fileName) {
      setStatus('No file selected');
      addLog('Select .hex or .bin file', 'error');
      return;
    }

    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setStatus('Preparing...');
    addLog('Starting firmware Update...', 'info');

    try {
      let binaryData: Uint8Array;

      const buffer = await file.arrayBuffer();
      binaryData = new Uint8Array(buffer);

      serialService.writeBytes(binaryData);

      setProgress(100);
      setStatus('Firmware uploaded OK');
      addLog('Firmware uploaded OK', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setStatus('Error occurred');
      addLog(`Firmware Update error: ${message}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  // Device is not in bootloader mode if connected but no bootloader message received (or timed out)
  const notInBootloader = isConnected && (!isInBootloader || bootloaderTimedOut);

  return (
    <Paper elevation={0} sx={{ p: 2, height: '100%', boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Bootloader Status - shown when not in bootloader mode */}
        {notInBootloader && (
          <Alert severity="warning">
            Device not in Firmware Update mode.<br/>
            Please select "Fimrware Update" from the main menu.
          </Alert>
        )}

        {/* Only show the rest of the UI when in bootloader mode */}
        {!notInBootloader && (
          <>
            {/* Bootloader detected message */}
            {isConnected && isInBootloader && (
              <Alert severity="success">
                Bootloader detected: {bootloaderVersion}
              </Alert>
            )}

            {/* File Selection */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="File Selected"
                value={fileName}
                InputProps={{ readOnly: true }}
                placeholder="No file selected"
              />

              <Button
                variant="outlined"
                onClick={handleBrowse}
                disabled={isUploading}
                startIcon={<UploadFileIcon />}
              >
                Browse
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".fwu"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Box>

            {/* Progress Bar - shows programming progress from bootloader */}
            <Box>
              <LinearProgress
                variant="determinate"
                value={programmingProgress}
                sx={{ height: 10, borderRadius: 5 }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                Programming: {programmingProgress}%
              </Typography>
            </Box>

            {/* Upload Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartUpdate}
              disabled={!fileName || isUploading || !isConnected}
              fullWidth
            >
              {isUploading ? 'Uploading...' : 'Start Update'}
            </Button>

            {/* Status */}
            <Alert severity={status.includes('OK') ? 'success' : 'info'}>
              {status}
            </Alert>
          </>
        )}
      </Box>
    </Paper>
  );
};
